var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// src/index.ts
import "reflect-metadata";

// src/injection-token.ts
var InjectionToken = class {
  static {
    __name(this, "InjectionToken");
  }
  description;
  options;
  constructor(description, options) {
    this.description = description;
    this.options = options;
  }
  /**
  * 返回令牌的字符串表示，用于调试
  */
  toString() {
    return `InjectionToken ${this.description}`;
  }
  /**
  * 获取令牌的工厂函数
  */
  get factory() {
    return this.options?.factory;
  }
};
var RecordInjectionToken = class extends InjectionToken {
  static {
    __name(this, "RecordInjectionToken");
  }
  constructor(description, options) {
    super(description, options);
  }
};
var MapInjectionToken = class extends InjectionToken {
  static {
    __name(this, "MapInjectionToken");
  }
  constructor(description, options) {
    super(description, options);
  }
};
var SetInjectionToken = class extends InjectionToken {
  static {
    __name(this, "SetInjectionToken");
  }
  constructor(description, options) {
    super(description, options);
  }
};

// src/host-attribute-token.ts
var HostAttributeToken = class _HostAttributeToken {
  static {
    __name(this, "HostAttributeToken");
  }
  attribute;
  defaultValue;
  /**
  * 令牌标识符
  */
  __token__ = "HostAttributeToken";
  /**
  * 创建宿主属性令牌
  *
  * @param attribute 属性名称
  * @param defaultValue 默认值（当属性不存在时使用）
  */
  constructor(attribute, defaultValue) {
    this.attribute = attribute;
    this.defaultValue = defaultValue;
  }
  /**
  * 返回令牌的字符串表示
  */
  toString() {
    return `HostAttributeToken(${this.attribute})`;
  }
  /**
  * 检查两个令牌是否相等
  *
  * @param other 其他令牌
  * @returns 是否相等
  */
  equals(other) {
    return other instanceof _HostAttributeToken && other.attribute === this.attribute;
  }
};
function isHostAttributeToken(token) {
  return token instanceof HostAttributeToken;
}
__name(isHostAttributeToken, "isHostAttributeToken");
function createHostAttributeToken(attribute, defaultValue) {
  return new HostAttributeToken(attribute, defaultValue);
}
__name(createHostAttributeToken, "createHostAttributeToken");

// src/injector.ts
function isType(val) {
  return typeof val === "function";
}
__name(isType, "isType");
var Injector = class {
  static {
    __name(this, "Injector");
  }
  parent;
  constructor(parent) {
    this.parent = parent;
  }
};

// src/null-injector.ts
var NullInjector = class extends Injector {
  static {
    __name(this, "NullInjector");
  }
  constructor() {
    super();
  }
  /**
  * 获取依赖实例，但总是抛出错误
  * @param token 注入令牌
  * @throws Error 总是抛出未找到提供者的错误
  */
  get(token, def) {
    if (def) return def;
    this.validateToken(token);
    let tokenName;
    if (typeof token === "function") {
      tokenName = token.name || JSON.stringify(token);
    } else if (token && typeof token === "object" && "toString" in token) {
      tokenName = token.toString();
    } else {
      tokenName = JSON.stringify(token);
    }
    console.error(`NullInjector: No Provider for "${tokenName}"`, {
      token,
      tokenType: typeof token,
      isBuiltIn: token === Object || token === Array || token === String || token === Number || token === Boolean
    });
    throw new Error(`NullInjector: \u672A\u627E\u5230 Provider for "${tokenName}"

\u53EF\u80FD\u7684\u539F\u56E0\uFF1A
1. \u5FD8\u8BB0\u5728\u6A21\u5757\u4E2D\u6CE8\u518C\u8BE5\u670D\u52A1
2. \u670D\u52A1\u672A\u4F7F\u7528 @Injectable() \u88C5\u9970\u5668
3. \u4F7F\u7528\u4E86\u5185\u7F6E\u7C7B\u578B\uFF08Object/Array \u7B49\uFF09\u800C\u975E\u5177\u4F53\u7684\u7C7B\u6216 InjectionToken`);
  }
  /**
  * 验证注入令牌是否合法
  * 阻止使用 Object 作为注入令牌（因为无法序列化成可读字符串）
  *
  * 允许的类型：
  * - 有 name 属性的（类、函数）- 可通过 name 定位
  * - string, symbol - 本身就是可读的
  * - InjectionToken - 有 toString() 方法
  */
  validateToken(token) {
    if (token === Object) {
      throw new Error(`\u4E0D\u5141\u8BB8\u4F7F\u7528\u5185\u7F6E\u7C7B\u578B "Object" \u4F5C\u4E3A\u6CE8\u5165\u4EE4\u724C\uFF01

Object \u65E0\u6CD5\u5E8F\u5217\u5316\u6210\u53EF\u8BFB\u7684\u8C03\u8BD5\u4FE1\u606F\u3002\u8BF7\u4F7F\u7528\u4EE5\u4E0B\u65B9\u5F0F\u4E4B\u4E00\uFF1A
1. \u521B\u5EFA\u5177\u4F53\u7684\u7C7B\u6216\u63A5\u53E3\uFF0C\u5982 class MyService {}
2. \u4F7F\u7528 InjectionToken\uFF0C\u5982 new InjectionToken<object>('my-config')
3. \u4F7F\u7528\u5B57\u7B26\u4E32\u4EE4\u724C\uFF0C\u5982 'MY_CONFIG'
4. \u4F7F\u7528 Symbol \u4EE4\u724C\uFF0C\u5982 Symbol('MY_CONFIG')`);
    }
  }
  set(_providers) {
    throw new Error(`NullInjector: No set method`);
  }
  async init() {
  }
  async destroy() {
  }
};

// src/injectable.ts
var INJECTABLE_METADATA_KEY = /* @__PURE__ */ Symbol("injectable");
function Injectable(options = {}) {
  return function(target) {
    Reflect.defineMetadata(INJECTABLE_METADATA_KEY, options, target);
    const providedIn = options.providedIn || "root";
    if (providedIn === "root") {
      const provide = target;
      if (options.useFactory) {
        root.set([
          {
            provide,
            useFactory: options.useFactory,
            deps: options.deps || []
          }
        ]);
      } else {
        root.set([
          {
            provide,
            useClass: target
          }
        ]);
      }
    }
    return target;
  };
}
__name(Injectable, "Injectable");
function getInjectableMetadata(target) {
  return Reflect.getMetadata(INJECTABLE_METADATA_KEY, target);
}
__name(getInjectableMetadata, "getInjectableMetadata");
function isInjectable(target) {
  return Reflect.hasMetadata(INJECTABLE_METADATA_KEY, target);
}
__name(isInjectable, "isInjectable");

// src/internal-inject-flags.ts
var InternalInjectFlags = /* @__PURE__ */ (function(InternalInjectFlags2) {
  InternalInjectFlags2[InternalInjectFlags2["Default"] = 0] = "Default";
  InternalInjectFlags2[InternalInjectFlags2["Optional"] = 1] = "Optional";
  InternalInjectFlags2[InternalInjectFlags2["SkipSelf"] = 2] = "SkipSelf";
  InternalInjectFlags2[InternalInjectFlags2["Self"] = 4] = "Self";
  InternalInjectFlags2[InternalInjectFlags2["Host"] = 8] = "Host";
  return InternalInjectFlags2;
})({});
function combineInjectFlags(...flags) {
  return flags.reduce((combined, flag) => combined | flag, 0);
}
__name(combineInjectFlags, "combineInjectFlags");
function hasFlag(flags, targetFlag) {
  if (targetFlag === 0) {
    return flags === 0;
  }
  return (flags & targetFlag) === targetFlag;
}
__name(hasFlag, "hasFlag");
function convertInjectOptionsToFlags(options) {
  if (!options) {
    return 0;
  }
  validateInjectOptionsConflicts(options);
  let flags = 0;
  if (options.optional) {
    flags |= 1;
  }
  if (options.skipSelf) {
    flags |= 2;
  }
  if (options.self) {
    flags |= 4;
  }
  if (options.host) {
    flags |= 8;
  }
  return flags;
}
__name(convertInjectOptionsToFlags, "convertInjectOptionsToFlags");
function validateInjectOptionsConflicts(options) {
  const conflicts = [];
  if (options.self && options.skipSelf) {
    conflicts.push({
      options: [
        "self",
        "skipSelf"
      ],
      reason: '"self" \u53EA\u5728\u5F53\u524D\u6CE8\u5165\u5668\u67E5\u627E\uFF0C\u800C "skipSelf" \u8DF3\u8FC7\u5F53\u524D\u6CE8\u5165\u5668\uFF0C\u4E24\u8005\u4E92\u76F8\u77DB\u76FE'
    });
  }
  if (options.self && options.host) {
    conflicts.push({
      options: [
        "self",
        "host"
      ],
      reason: '"self" \u53EA\u5728\u5F53\u524D\u6CE8\u5165\u5668\u67E5\u627E\uFF0C\u800C "host" \u5728\u5BBF\u4E3B\u6CE8\u5165\u5668\u67E5\u627E\uFF0C\u4E24\u8005\u4E92\u76F8\u77DB\u76FE'
    });
  }
  if (options.skipSelf && options.host) {
    if (typeof console !== "undefined" && console.warn) {
      console.warn('InjectOptions \u8B66\u544A: \u540C\u65F6\u4F7F\u7528 "skipSelf" \u548C "host" \u53EF\u80FD\u4E0D\u662F\u9884\u671F\u7684\u884C\u4E3A\u3002"skipSelf" \u4F1A\u88AB "host" \u7684\u884C\u4E3A\u8986\u76D6\u3002');
    }
  }
  if (conflicts.length > 0) {
    const errorMessages = conflicts.map((conflict) => `- ${conflict.options.join(" + ")}: ${conflict.reason}`).join("\n");
    throw new Error(`InjectOptions \u9009\u9879\u51B2\u7A81:
${errorMessages}

\u8BF7\u68C0\u67E5\u6CE8\u5165\u9009\u9879\u7684\u7EC4\u5408\u662F\u5426\u6B63\u786E\u3002`);
  }
}
__name(validateInjectOptionsConflicts, "validateInjectOptionsConflicts");
function convertFlagsToInjectOptions(flags) {
  const options = {};
  if (hasFlag(flags, 1)) {
    options.optional = true;
  }
  if (hasFlag(flags, 2)) {
    options.skipSelf = true;
  }
  if (hasFlag(flags, 4)) {
    options.self = true;
  }
  if (hasFlag(flags, 8)) {
    options.host = true;
  }
  return options;
}
__name(convertFlagsToInjectOptions, "convertFlagsToInjectOptions");
function flagsToString(flags) {
  if (flags === 0) {
    return "Default";
  }
  const flagNames = [];
  if (hasFlag(flags, 1)) {
    flagNames.push("Optional");
  }
  if (hasFlag(flags, 2)) {
    flagNames.push("SkipSelf");
  }
  if (hasFlag(flags, 4)) {
    flagNames.push("Self");
  }
  if (hasFlag(flags, 8)) {
    flagNames.push("Host");
  }
  return flagNames.join(" | ");
}
__name(flagsToString, "flagsToString");

// src/inject.ts
var INJECT_METADATA_KEY = /* @__PURE__ */ Symbol("inject-unified");
var LEGACY_INJECT_METADATA_KEY = /* @__PURE__ */ Symbol("inject");
var LEGACY_INJECT_OPTIONS_METADATA_KEY = /* @__PURE__ */ Symbol("inject-options");
var PARAM_TYPES_KEY = "design:paramtypes";
function Inject(token, options) {
  return function(target, _propertyKey, parameterIndex) {
    const existingMetadata = Reflect.getMetadata(INJECT_METADATA_KEY, target) || [];
    while (existingMetadata.length <= parameterIndex) {
      existingMetadata.push({
        token: void 0,
        flags: InternalInjectFlags.Default
      });
    }
    existingMetadata[parameterIndex] = {
      token,
      flags: convertInjectOptionsToFlags(options)
    };
    Reflect.defineMetadata(INJECT_METADATA_KEY, existingMetadata, target);
  };
}
__name(Inject, "Inject");
function getInjectMetadata(target) {
  const unifiedMetadata = Reflect.getMetadata(INJECT_METADATA_KEY, target);
  const legacyTokens = Reflect.getMetadata(LEGACY_INJECT_METADATA_KEY, target);
  const paramTypes = Reflect.getMetadata(PARAM_TYPES_KEY, target);
  if (!unifiedMetadata && !legacyTokens && !paramTypes) {
    return void 0;
  }
  const maxLength = Math.max(unifiedMetadata?.length || 0, legacyTokens?.length || 0, paramTypes?.length || 0);
  if (maxLength === 0) {
    return void 0;
  }
  const result = [];
  for (let i = 0; i < maxLength; i++) {
    if (unifiedMetadata && unifiedMetadata[i] && unifiedMetadata[i]?.token !== void 0) {
      result[i] = unifiedMetadata[i].token;
    } else if (legacyTokens && legacyTokens[i] !== void 0) {
      result[i] = legacyTokens[i];
    } else if (paramTypes && paramTypes[i] !== void 0) {
      result[i] = paramTypes[i];
    } else {
      result[i] = void 0;
    }
  }
  return result;
}
__name(getInjectMetadata, "getInjectMetadata");
function getInjectOptionsMetadata(target) {
  const unifiedMetadata = Reflect.getMetadata(INJECT_METADATA_KEY, target);
  if (unifiedMetadata) {
    return unifiedMetadata.map((metadata) => {
      if (!metadata || metadata.flags === InternalInjectFlags.Default) {
        return {};
      }
      const options = {};
      if (metadata.flags & InternalInjectFlags.Optional) {
        options.optional = true;
      }
      if (metadata.flags & InternalInjectFlags.SkipSelf) {
        options.skipSelf = true;
      }
      if (metadata.flags & InternalInjectFlags.Self) {
        options.self = true;
      }
      if (metadata.flags & InternalInjectFlags.Host) {
        options.host = true;
      }
      return options;
    });
  }
  return Reflect.getMetadata(LEGACY_INJECT_OPTIONS_METADATA_KEY, target);
}
__name(getInjectOptionsMetadata, "getInjectOptionsMetadata");
function getUnifiedInjectMetadata(target) {
  const unifiedMetadata = Reflect.getMetadata(INJECT_METADATA_KEY, target);
  if (unifiedMetadata) {
    return unifiedMetadata;
  }
  const legacyTokens = Reflect.getMetadata(LEGACY_INJECT_METADATA_KEY, target);
  const legacyOptions = Reflect.getMetadata(LEGACY_INJECT_OPTIONS_METADATA_KEY, target);
  const paramTypes = Reflect.getMetadata(PARAM_TYPES_KEY, target);
  if (!legacyTokens && !paramTypes) {
    return void 0;
  }
  const maxLength = Math.max(legacyTokens?.length || 0, legacyOptions?.length || 0, paramTypes?.length || 0);
  if (maxLength === 0) {
    return void 0;
  }
  const result = [];
  for (let i = 0; i < maxLength; i++) {
    let token;
    if (legacyTokens && legacyTokens[i] !== void 0) {
      token = legacyTokens[i];
    } else if (paramTypes && paramTypes[i] !== void 0) {
      token = paramTypes[i];
    } else {
      token = void 0;
    }
    const options = legacyOptions?.[i] || {};
    const flags = convertInjectOptionsToFlags(options);
    result[i] = {
      token,
      flags
    };
  }
  return result;
}
__name(getUnifiedInjectMetadata, "getUnifiedInjectMetadata");
function hasInjectMetadata(target) {
  return Reflect.hasMetadata(INJECT_METADATA_KEY, target) || Reflect.hasMetadata(LEGACY_INJECT_METADATA_KEY, target) || Reflect.hasMetadata(PARAM_TYPES_KEY, target);
}
__name(hasInjectMetadata, "hasInjectMetadata");

// src/lifecycle.ts
function isOnDestroy(obj) {
  if (!obj) return false;
  return typeof obj.onDestroy === "function";
}
__name(isOnDestroy, "isOnDestroy");

// src/forward-ref.ts
var ForwardRef = class {
  static {
    __name(this, "ForwardRef");
  }
  forwardRefFn;
  /**
  * ForwardRef 的唯一标识符，用于识别这是一个ForwardRef对象
  */
  __forward_ref__ = true;
  constructor(forwardRefFn) {
    this.forwardRefFn = forwardRefFn;
  }
  /**
  * 解析ForwardRef，执行包装的函数获取实际的令牌
  */
  resolve() {
    try {
      const result = this.forwardRefFn();
      if (result === void 0 || result === null) {
        throw new Error("\u65E0\u6548\u7684forwardRef: forwardRef\u51FD\u6570\u8FD4\u56DE\u4E86undefined\u6216null");
      }
      return result;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`forwardRef\u89E3\u6790\u5931\u8D25: ${error.message}`);
      }
      throw error;
    }
  }
  /**
  * 提供toString方法用于调试
  */
  toString() {
    return "ForwardRef";
  }
};
function forwardRef(forwardRefFn) {
  return new ForwardRef(forwardRefFn);
}
__name(forwardRef, "forwardRef");
function isForwardRef(value) {
  return value && typeof value === "object" && value.__forward_ref__ === true;
}
__name(isForwardRef, "isForwardRef");
function resolveForwardRef(tokenOrForwardRef) {
  if (isForwardRef(tokenOrForwardRef)) {
    return tokenOrForwardRef.resolve();
  }
  return tokenOrForwardRef;
}
__name(resolveForwardRef, "resolveForwardRef");
var forwardRefCache = /* @__PURE__ */ new WeakMap();
function resolveForwardRefCached(tokenOrForwardRef) {
  if (isForwardRef(tokenOrForwardRef)) {
    if (forwardRefCache.has(tokenOrForwardRef)) {
      return forwardRefCache.get(tokenOrForwardRef);
    }
    const resolved = tokenOrForwardRef.resolve();
    forwardRefCache.set(tokenOrForwardRef, resolved);
    return resolved;
  }
  return tokenOrForwardRef;
}
__name(resolveForwardRefCached, "resolveForwardRefCached");
function resolveForwardRefsInArray(items) {
  return items.map((item) => resolveForwardRefCached(item));
}
__name(resolveForwardRefsInArray, "resolveForwardRefsInArray");
function resolveForwardRefsInDeps(deps) {
  if (!deps) {
    return deps;
  }
  return resolveForwardRefsInArray(deps);
}
__name(resolveForwardRefsInDeps, "resolveForwardRefsInDeps");

// src/on-init.ts
var ON_INIT_METADATA_KEY = /* @__PURE__ */ Symbol("onInit");
function OnInit() {
  return function(target) {
    Reflect.defineMetadata(ON_INIT_METADATA_KEY, true, target);
    return target;
  };
}
__name(OnInit, "OnInit");
function hasOnInitMetadata(target) {
  if (!target || typeof target !== "function") {
    return false;
  }
  return Reflect.getMetadata(ON_INIT_METADATA_KEY, target) === true;
}
__name(hasOnInitMetadata, "hasOnInitMetadata");
function isOnInit(obj) {
  if (!obj) return false;
  return typeof obj.onInit === "function";
}
__name(isOnInit, "isOnInit");

// src/app-initializer.ts
var APP_INITIALIZER = new InjectionToken("APP_INITIALIZER");

// src/initializer-graph.ts
var InitializerGraph = class {
  static {
    __name(this, "InitializerGraph");
  }
  nodes = /* @__PURE__ */ new Map();
  addNode(token, fn, options) {
    this.nodes.set(token, {
      token,
      fn,
      dependencies: options.dependencies
    });
  }
  topologicalSort() {
    const sorted = [];
    const visited = /* @__PURE__ */ new Set();
    const visiting = /* @__PURE__ */ new Set();
    const nodeArray = Array.from(this.nodes.values());
    const visit = /* @__PURE__ */ __name((node, path) => {
      if (visited.has(node.token)) return;
      if (visiting.has(node.token)) {
        const cycle = [
          ...path,
          node.token
        ].map((t) => this.tokenToString(t)).join(" -> ");
        throw new Error(`\u68C0\u6D4B\u5230\u521D\u59CB\u5316\u5668\u5FAA\u73AF\u4F9D\u8D56: ${cycle}`);
      }
      visiting.add(node.token);
      path.push(node.token);
      for (const depToken of node.dependencies) {
        const depNode = this.findNodeByDependency(depToken);
        if (depNode) {
          visit(depNode, [
            ...path
          ]);
        }
      }
      visiting.delete(node.token);
      path.pop();
      visited.add(node.token);
      sorted.push(node);
    }, "visit");
    for (const node of nodeArray) {
      if (!visited.has(node.token)) {
        visit(node, []);
      }
    }
    return sorted;
  }
  async execute() {
    const sorted = this.topologicalSort();
    for (const node of sorted) {
      try {
        await node.fn();
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : String(error);
        const tokenStr = this.tokenToString(node.token);
        throw new Error(`\u521D\u59CB\u5316\u5668\u6267\u884C\u5931\u8D25 [${tokenStr}]: ${errorMsg}`);
      }
    }
  }
  findNodeByDependency(depToken) {
    for (const node of this.nodes.values()) {
      if (this.tokensMatch(node.token, depToken)) {
        return node;
      }
    }
    return void 0;
  }
  tokensMatch(token1, token2) {
    if (token1 === token2) return true;
    if (token1?.constructor === token2?.constructor) return true;
    return false;
  }
  tokenToString(token) {
    if (token?.toString && token.toString !== Object.prototype.toString) {
      return token.toString();
    }
    if (typeof token === "function") {
      return token.name || "AnonymousToken";
    }
    return String(token);
  }
};

// src/environment-injector-utils.ts
var EnvironmentInjectorUtils = class _EnvironmentInjectorUtils {
  static {
    __name(this, "EnvironmentInjectorUtils");
  }
  /**
  * 获取令牌的可读名称，用于错误消息
  */
  static getTokenName(token) {
    if (typeof token === "string") {
      return token;
    }
    if (typeof token === "symbol") {
      return token.toString();
    }
    if (typeof token === "function") {
      return token.name || "anonymous";
    }
    if (token && typeof token === "object" && token.toString) {
      return token.toString();
    }
    return String(token);
  }
  /**
  * 获取令牌类型
  */
  static getTokenType(token) {
    if (typeof token === "string") {
      return "string";
    }
    if (typeof token === "symbol") {
      return "symbol";
    }
    if (typeof token === "function") {
      return "class";
    }
    if (token && typeof token === "object" && token.toString) {
      return "InjectionToken";
    }
    return "unknown";
  }
  /**
  * 获取提供者类型
  */
  static getProviderType(provider) {
    if ("useValue" in provider) {
      return "ValueProvider";
    }
    if ("useClass" in provider) {
      return "ClassProvider";
    }
    if ("useFactory" in provider) {
      return "FactoryProvider";
    }
    if ("useExisting" in provider) {
      return "ExistingProvider";
    }
    if ("useLazyClass" in provider) {
      return "LazyClassProvider";
    }
    if ("useLazyFactory" in provider) {
      return "LazyFactoryProvider";
    }
    return "ConstructorProvider";
  }
  /**
  * 检查是否为多值提供者（数组或 Record）
  */
  static isMultiProvider(providers) {
    return providers.some((p) => p.multi);
  }
  /**
  * 检查是否为 Record 类型多值提供者
  */
  static isRecordProvider(providers) {
    return providers.some((p) => p.multi === "record");
  }
  /**
  * 检查是否为数组类型多值提供者
  */
  static isArrayProvider(providers) {
    return providers.some((p) => p.multi === true);
  }
  /**
  * 检查是否为 Map 类型多值提供者
  */
  static isMapProvider(providers) {
    return providers.some((p) => p.multi === "map");
  }
  /**
  * 检查是否为 Set 类型多值提供者
  */
  static isSetProvider(providers) {
    return providers.some((p) => p.multi === "set");
  }
  /**
  * 验证 Record Provider 配置
  */
  static validateRecordProvider(provider) {
    if (provider.multi === "record" && !provider.key) {
      throw new Error(`Record provider for ${this.getTokenName(provider.provide)} requires a 'key' field`);
    }
  }
  /**
  * 验证 Map Provider 配置
  */
  static validateMapProvider(provider) {
    if (provider.multi === "map" && provider.mapKey === void 0) {
      throw new Error(`Map provider for ${this.getTokenName(provider.provide)} requires a 'mapKey' field`);
    }
  }
  /**
  * 验证 Provider 一致性（不允许混合数组、Record、Map 和 Set）
  */
  static validateProviderConsistency(providers) {
    const hasArray = providers.some((p) => p.multi === true);
    const hasRecord = providers.some((p) => p.multi === "record");
    const hasMap = providers.some((p) => p.multi === "map");
    const hasSet = providers.some((p) => p.multi === "set");
    const modes = [
      hasArray,
      hasRecord,
      hasMap,
      hasSet
    ].filter(Boolean).length;
    if (modes > 1) {
      throw new Error("Cannot mix different multi provider types (array/record/map/set) for the same token");
    }
  }
  /**
  * 验证注入选项的互斥性
  */
  static validateInjectOptions(options) {
    if (options.self && options.skipSelf) {
      throw new Error("InjectOptions: self \u548C skipSelf \u9009\u9879\u4E0D\u80FD\u540C\u65F6\u4F7F\u7528");
    }
    if (options.host && (options.self || options.skipSelf)) {
      throw new Error("InjectOptions: host \u9009\u9879\u4E0D\u80FD\u4E0E self \u6216 skipSelf \u540C\u65F6\u4F7F\u7528");
    }
  }
  /**
  * 生成循环依赖错误消息
  */
  static generateCircularDependencyError(currentToken, dependencyPath, getTokenName = _EnvironmentInjectorUtils.getTokenName) {
    const tokenName = getTokenName(currentToken);
    const pathStr = dependencyPath.map((t) => getTokenName(t)).join(" -> ");
    return new Error(`\u68C0\u6D4B\u5230\u5FAA\u73AF\u4F9D\u8D56: ${pathStr} -> ${tokenName}`);
  }
  /**
  * 生成唯一的注入器ID
  */
  static generateInjectorId() {
    return `injector_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }
};

// src/environment-injector.ts
var EnvironmentInjector = class _EnvironmentInjector extends Injector {
  static {
    __name(this, "EnvironmentInjector");
  }
  instances = /* @__PURE__ */ new Map();
  providers = /* @__PURE__ */ new Map();
  autoResolvedClasses = /* @__PURE__ */ new Set();
  resolvingTokens = /* @__PURE__ */ new Set();
  dependencyPath = [];
  isDestroyed = false;
  /**
  * 注入器作用域，决定如何处理 providedIn 服务
  */
  scope;
  constructor(providers, parent, scope = "root") {
    super(parent || new NullInjector());
    this.scope = scope;
    this.setupProviders([
      ...providers,
      {
        provide: Injector,
        useValue: this
      }
    ]);
  }
  /**
  * 创建支持自动提供者解析的环境注入器
  *
  * 这个静态方法会自动解析标记为 @Injectable({ providedIn: 'auto' }) 等的服务，
  * 无需手动在 providers 数组中注册这些服务。
  *
  * @param manualProviders 手动注册的提供者数组
  * @param parent 可选的父注入器
  * @param scope 注入器作用域，默认为 'auto'
  * @returns 新的环境注入器实例
  */
  static createWithAutoProviders(manualProviders, parent, scope = "auto") {
    return new _EnvironmentInjector(manualProviders, parent, scope);
  }
  /**
  * 全局根注入器实例（单例）
  */
  static rootInjectorInstance = null;
  /**
  * 全局平台注入器实例（单例）
  */
  static platformInjectorInstance = null;
  /**
  * 创建根注入器（全局单例）
  *
  * 根注入器是基础层注入器，全局唯一，通常作为平台注入器的父级。
  * 标记为 @Injectable({ providedIn: 'root' }) 的服务会在此注入器中注册。
  *
  * @param providers 根级提供者数组
  * @returns 全局唯一的根注入器实例
  * @throws Error 如果根注入器已经存在
  */
  static createRootInjector(providers = []) {
    if (this.rootInjectorInstance) {
      throw new Error("Root injector already exists! Root injector must be globally unique.");
    }
    this.rootInjectorInstance = new _EnvironmentInjector(providers, new NullInjector(), "root");
    return this.rootInjectorInstance;
  }
  /**
  * 获取全局根注入器实例
  *
  * @returns 根注入器实例，如果不存在则返回 null
  */
  static getRootInjector() {
    return this.rootInjectorInstance;
  }
  /**
  * 重置平台注入器（主要用于测试）
  *
  * @internal 仅供内部使用
  */
  static resetPlatformInjector() {
    if (this.platformInjectorInstance) {
      this.platformInjectorInstance.destroy();
      this.platformInjectorInstance = null;
    }
  }
  /**
  * 重置根注入器和平台注入器（主要用于测试）
  *
  * @internal 仅供内部使用
  */
  static resetRootInjector() {
    this.resetPlatformInjector();
    if (this.rootInjectorInstance) {
      this.rootInjectorInstance.destroy();
      this.rootInjectorInstance = null;
    }
  }
  /**
  * 创建平台注入器（全局单例）
  *
  * 平台注入器用于存储跨应用共享的服务，全局唯一，自动使用全局根注入器作为父级。
  * 标记为 @Injectable({ providedIn: 'platform' }) 的服务会在此注入器中注册。
  *
  * @param providers 平台级提供者数组
  * @returns 全局唯一的平台注入器实例
  * @throws Error 如果平台注入器已经存在或全局根注入器不存在
  */
  static createPlatformInjector(providers = []) {
    if (this.platformInjectorInstance) {
      throw new Error("Platform injector already exists! Platform injector must be globally unique.");
    }
    const globalRootInjector = this.getRootInjector();
    if (!globalRootInjector) {
      throw new Error("Root injector not found! Please create a root injector first using createRootInjector() before creating platform injector.");
    }
    this.platformInjectorInstance = new _EnvironmentInjector(providers, globalRootInjector, "platform");
    return this.platformInjectorInstance;
  }
  /**
  * 获取全局平台注入器实例
  *
  * @returns 平台注入器实例，如果不存在则返回 null
  */
  static getPlatformInjector() {
    return this.platformInjectorInstance;
  }
  /**
  * 创建应用注入器
  *
  * 应用注入器以全局平台注入器为父级，用于存储应用级的服务。
  * 标记为 @Injectable({ providedIn: 'application' }) 的服务会在此注入器中注册。
  *
  * @param providers 应用级提供者数组
  * @returns 新的应用注入器实例
  * @throws Error 如果全局平台注入器不存在
  */
  static createApplicationInjector(providers = []) {
    const globalPlatformInjector = this.getPlatformInjector();
    if (!globalPlatformInjector) {
      throw new Error("Platform injector not found! Please create a platform injector first using createPlatformInjector() before creating application injector.");
    }
    return new _EnvironmentInjector(providers, globalPlatformInjector, "application");
  }
  /**
  * 创建功能模块注入器
  *
  * 功能注入器通常以应用注入器为父级，用于存储功能模块级的服务。
  *
  * @param providers 功能模块级提供者数组
  * @param parentInjector 父注入器（通常是应用注入器）
  * @returns 新的功能注入器实例
  */
  static createFeatureInjector(providers, parentInjector) {
    return new _EnvironmentInjector(providers, parentInjector, "feature");
  }
  /**
  * 获取指定令牌的依赖实例
  * @param token 注入令牌
  * @returns 依赖实例
  */
  get(token, def) {
    const resolvedToken = resolveForwardRefCached(token);
    this.validateToken(resolvedToken);
    const tokenName = this.getTokenName(resolvedToken);
    if (this.isDestroyed) {
      throw new Error("\u6CE8\u5165\u5668\u5DF2\u9500\u6BC1");
    }
    if (this.instances.has(resolvedToken)) {
      const instance = this.instances.get(resolvedToken);
      return instance;
    }
    if (this.resolvingTokens.has(resolvedToken)) {
      const pathStr = this.dependencyPath.map((t) => this.getTokenName(t)).join(" -> ");
      const errorMessage = `\u68C0\u6D4B\u5230\u5FAA\u73AF\u4F9D\u8D56: ${pathStr} -> ${tokenName}`;
      throw new Error(errorMessage);
    }
    this.resolvingTokens.add(resolvedToken);
    this.dependencyPath.push(resolvedToken);
    try {
      let result;
      const tokenProviders = this.providers.get(resolvedToken);
      if (tokenProviders) {
        result = this.createInstance(resolvedToken, tokenProviders);
        if (!EnvironmentInjectorUtils.isMultiProvider(tokenProviders)) {
          this.instances.set(resolvedToken, result);
        }
      } else {
        const autoProvider = this.tryAutoResolveProvider(resolvedToken);
        if (autoProvider) {
          this.providers.set(resolvedToken, [
            autoProvider
          ]);
          result = this.createInstance(resolvedToken, [
            autoProvider
          ]);
          this.instances.set(resolvedToken, result);
        } else {
          result = this.parent.get(resolvedToken, def);
        }
      }
      return result;
    } finally {
      this.resolvingTokens.delete(resolvedToken);
      this.dependencyPath.pop();
    }
  }
  /**
  * 设置提供者映射
  */
  setupProviders(providers) {
    providers.forEach((provider) => {
      this.validateToken(provider.provide);
      const existing = this.providers.get(provider.provide) || [];
      const updated = [
        ...existing,
        provider
      ];
      EnvironmentInjectorUtils.validateProviderConsistency(updated);
      this.providers.set(provider.provide, updated);
    });
  }
  set(providers) {
    const list = providers.map((it) => {
      if (isType(it)) {
        return {
          provide: it,
          useClass: it
        };
      }
      return it;
    });
    this.setupProviders(list);
  }
  /**
  * 根据提供者创建实例
  */
  createInstance(_token, providers) {
    if (EnvironmentInjectorUtils.isSetProvider(providers)) {
      const set = /* @__PURE__ */ new Set();
      for (const p of providers) {
        if (p.multi !== "set") continue;
        set.add(this.createSingleInstance(p));
      }
      return set;
    }
    if (EnvironmentInjectorUtils.isMapProvider(providers)) {
      const map = /* @__PURE__ */ new Map();
      for (const p of providers) {
        if (p.multi !== "map") continue;
        EnvironmentInjectorUtils.validateMapProvider(p);
        map.set(p.mapKey, this.createSingleInstance(p));
      }
      return map;
    }
    if (EnvironmentInjectorUtils.isRecordProvider(providers)) {
      const record = {};
      for (const p of providers) {
        if (p.multi !== "record") continue;
        EnvironmentInjectorUtils.validateRecordProvider(p);
        record[p.key] = this.createSingleInstance(p);
      }
      return record;
    }
    if (EnvironmentInjectorUtils.isArrayProvider(providers)) {
      return providers.filter((p) => p.multi === true).map((p) => this.createSingleInstance(p));
    }
    const lastProvider = providers[providers.length - 1];
    if (!lastProvider) {
      throw new Error(`No provider found for token: ${String(_token)}`);
    }
    return this.createSingleInstance(lastProvider);
  }
  /**
  * 根据单个提供者创建实例
  */
  createSingleInstance(provider) {
    if ("useValue" in provider) {
      return provider.useValue;
    }
    if ("useClass" in provider) {
      const resolvedClass = resolveForwardRefCached(provider.useClass);
      return this.createInstanceWithDI(resolvedClass);
    }
    if ("useFactory" in provider) {
      const resolvedDeps = resolveForwardRefsInDeps(provider.deps);
      const deps = (resolvedDeps || []).map((dep) => this.get(dep));
      return provider.useFactory(...deps);
    }
    if ("useExisting" in provider) {
      const resolvedExisting = resolveForwardRefCached(provider.useExisting);
      return this.get(resolvedExisting);
    }
    return this.createInstanceWithDI(provider.provide);
  }
  /**
  * 使用依赖注入创建类实例
  */
  createInstanceWithDI(ClassConstructor) {
    const injectMetadata = getInjectMetadata(ClassConstructor);
    const injectOptions = getInjectOptionsMetadata(ClassConstructor);
    if (!injectMetadata || injectMetadata.length === 0) {
      const instance2 = new ClassConstructor();
      return instance2;
    }
    const dependencies = injectMetadata.map((token, index) => {
      if (token === void 0) {
        throw new Error(`Cannot resolve dependency at index ${index} for ${ClassConstructor.name}. Make sure to use @Inject() decorator.`);
      }
      const options = injectOptions?.[index] || {};
      const resolvedToken = resolveForwardRefCached(token);
      return this.resolveDependency(resolvedToken, options);
    });
    const instance = new ClassConstructor(...dependencies);
    return instance;
  }
  /**
  * 实际的依赖解析实现
  */
  resolveDependency(token, optionsOrFlags) {
    let flags;
    if (typeof optionsOrFlags === "number") {
      flags = optionsOrFlags;
    } else {
      EnvironmentInjectorUtils.validateInjectOptions(optionsOrFlags);
      flags = convertInjectOptionsToFlags(optionsOrFlags);
    }
    try {
      if (hasFlag(flags, InternalInjectFlags.SkipSelf)) {
        return this.parent.get(token);
      }
      if (hasFlag(flags, InternalInjectFlags.Self)) {
        return this.getSelf(token);
      }
      if (hasFlag(flags, InternalInjectFlags.Host)) {
        return this.getFromHost(token);
      }
      return this.get(token);
    } catch (error) {
      if (hasFlag(flags, InternalInjectFlags.Optional)) {
        return null;
      }
      throw error;
    }
  }
  /**
  * 只在当前注入器中查找，不查找父注入器
  */
  getSelf(token) {
    if (this.isDestroyed) {
      throw new Error("\u6CE8\u5165\u5668\u5DF2\u9500\u6BC1");
    }
    if (this.instances.has(token)) {
      return this.instances.get(token);
    }
    if (this.resolvingTokens.has(token)) {
      const tokenName = this.getTokenName(token);
      const pathStr = this.dependencyPath.map((t) => this.getTokenName(t)).join(" -> ");
      throw new Error(`\u68C0\u6D4B\u5230\u5FAA\u73AF\u4F9D\u8D56: ${pathStr} -> ${tokenName}`);
    }
    this.resolvingTokens.add(token);
    this.dependencyPath.push(token);
    try {
      const tokenProviders = this.providers.get(token);
      if (tokenProviders) {
        const result = this.createInstance(token, tokenProviders);
        if (!EnvironmentInjectorUtils.isMultiProvider(tokenProviders)) {
          this.instances.set(token, result);
        }
        return result;
      }
      const autoProvider = this.tryAutoResolveProvider(token);
      if (autoProvider) {
        this.providers.set(token, [
          autoProvider
        ]);
        const result = this.createInstance(token, [
          autoProvider
        ]);
        this.instances.set(token, result);
        return result;
      }
      const tokenName = this.getTokenName(token);
      throw new Error(`No provider for ${tokenName}!`);
    } finally {
      this.resolvingTokens.delete(token);
      this.dependencyPath.pop();
    }
  }
  /**
  * 在宿主注入器（根注入器）中查找依赖
  * host 选项查找最顶层的父注入器，如果没有父注入器，则查找不到
  */
  getFromHost(token) {
    let hostInjector = this;
    while (hostInjector.parent && !(hostInjector.parent instanceof NullInjector)) {
      hostInjector = hostInjector.parent;
    }
    if (hostInjector === this) {
      const tokenName = this.getTokenName(token);
      throw new Error(`No provider for ${tokenName}!`);
    }
    if (hostInjector instanceof _EnvironmentInjector) {
      return hostInjector.get(token);
    }
    return hostInjector.get(token);
  }
  /**
  * 初始化注入器，调用所有标记 @OnInit() 的服务的 onModelInit() 方法
  *
  * 策略：
  * 1. 执行所有 APP_INITIALIZER（按依赖顺序）
  * 2. 初始化所有 @OnInit 服务
  *
  * 优势：
  * - 支持初始化顺序控制
  * - 严格的错误处理
  * - 避免过早实例化：只创建标记 @OnInit() 的服务
  * - 零运行时开销：基于编译时元数据
  */
  async init() {
    await this.runAppInitializers();
    await this.runOnInitServices();
  }
  async runAppInitializers() {
    const initializers = this.get(APP_INITIALIZER, []) || [];
    if (initializers.length === 0) {
      return;
    }
    const graph = new InitializerGraph();
    for (const initializer of initializers) {
      const token = initializer.provide || initializer;
      const initFn = /* @__PURE__ */ __name(() => initializer.init(), "initFn");
      const dependencies = new Set(initializer.deps || []);
      graph.addNode(token, initFn, {
        dependencies
      });
    }
    await graph.execute();
  }
  async runOnInitServices() {
    const initializedInstances = /* @__PURE__ */ new Set();
    for (const instance of this.instances.values()) {
      if (isOnInit(instance)) {
        await this.initInstance(instance);
        initializedInstances.add(instance);
      }
    }
    for (const [token, providers] of this.providers.entries()) {
      for (const provider of providers) {
        const targetClass = this.extractClassFromProvider(provider);
        if (targetClass && hasOnInitMetadata(targetClass)) {
          const instance = this.get(token);
          if (!initializedInstances.has(instance) && isOnInit(instance)) {
            await this.initInstance(instance);
            initializedInstances.add(instance);
          }
        }
      }
    }
  }
  /**
  * 从 Provider 中提取类定义
  */
  extractClassFromProvider(provider) {
    if ("useClass" in provider) {
      return resolveForwardRefCached(provider.useClass);
    }
    if ("useFactory" in provider && typeof provider.provide === "function") {
      return provider.provide;
    }
    if (!("useValue" in provider) && !("useFactory" in provider) && !("useExisting" in provider) && typeof provider.provide === "function") {
      return provider.provide;
    }
    return null;
  }
  /**
  * 初始化单个实例（严格模式）
  */
  async initInstance(instance) {
    try {
      await instance.onInit();
    } catch (error) {
      const instanceName = instance.constructor?.name || "Unknown";
      const errorMsg = error instanceof Error ? error.message : String(error);
      throw new Error(`@OnInit \u670D\u52A1\u521D\u59CB\u5316\u5931\u8D25 [${instanceName}]: ${errorMsg}`);
    }
  }
  /**
  * 销毁注入器，清理所有实例并调用 OnDestroy 生命周期钩子
  */
  async destroy() {
    if (this.isDestroyed) {
      return;
    }
    this.isDestroyed = true;
    for (const instance of this.instances.values()) {
      await this.destroyInstance(instance);
    }
    this.instances.clear();
    this.resolvingTokens.clear();
    this.dependencyPath.length = 0;
  }
  /**
  * 销毁单个实例
  */
  async destroyInstance(instance) {
    try {
      if (isOnDestroy(instance)) {
        await instance.onDestroy();
      }
    } catch (error) {
    }
  }
  /**
  * 获取令牌的可读名称，用于错误消息
  */
  getTokenName(token) {
    return EnvironmentInjectorUtils.getTokenName(token);
  }
  /**
  * 验证注入令牌是否合法
  * 阻止使用 Object 作为注入令牌（因为无法序列化成可读字符串）
  *
  * 允许的类型：
  * - 有 name 属性的（类、函数）- 可通过 name 定位
  * - string, symbol - 本身就是可读的
  * - InjectionToken - 有 toString() 方法
  */
  validateToken(token) {
    if (token === Object) {
      throw new Error(`\u4E0D\u5141\u8BB8\u4F7F\u7528\u5185\u7F6E\u7C7B\u578B "Object" \u4F5C\u4E3A\u6CE8\u5165\u4EE4\u724C\uFF01

Object \u65E0\u6CD5\u5E8F\u5217\u5316\u6210\u53EF\u8BFB\u7684\u8C03\u8BD5\u4FE1\u606F\u3002\u8BF7\u4F7F\u7528\u4EE5\u4E0B\u65B9\u5F0F\u4E4B\u4E00\uFF1A
1. \u521B\u5EFA\u5177\u4F53\u7684\u7C7B\u6216\u63A5\u53E3\uFF0C\u5982 class MyService {}
2. \u4F7F\u7528 InjectionToken\uFF0C\u5982 new InjectionToken<object>('my-config')
3. \u4F7F\u7528\u5B57\u7B26\u4E32\u4EE4\u724C\uFF0C\u5982 'MY_CONFIG'
4. \u4F7F\u7528 Symbol \u4EE4\u724C\uFF0C\u5982 Symbol('MY_CONFIG')`);
    }
  }
  /**
  * 尝试自动解析 providedIn 服务
  */
  tryAutoResolveProvider(token) {
    if (token && typeof token === "object" && "factory" in token) {
      const factory = token.factory;
      if (factory && typeof factory === "function") {
        if (this.autoResolvedClasses.has(token)) {
          return null;
        }
        this.autoResolvedClasses.add(token);
        return {
          provide: token,
          useFactory: factory,
          deps: []
        };
      }
      return null;
    }
    if (typeof token !== "function") {
      return null;
    }
    if (this.autoResolvedClasses.has(token)) {
      return null;
    }
    const metadata = getInjectableMetadata(token);
    if (!metadata || metadata.providedIn === null) {
      return null;
    }
    if (!this.shouldAutoResolve(metadata.providedIn)) {
      return null;
    }
    this.autoResolvedClasses.add(token);
    if (metadata.useFactory) {
      return {
        provide: token,
        useFactory: metadata.useFactory,
        deps: metadata.deps || []
      };
    } else {
      return {
        provide: token,
        useClass: token
      };
    }
  }
  /**
  * 判断是否应该在当前注入器中自动解析指定作用域的服务
  */
  shouldAutoResolve(providedIn) {
    if (providedIn === null) {
      return false;
    }
    return providedIn === "auto" || this.scope === providedIn;
  }
};
var root = EnvironmentInjector.createRootInjector([]);

// src/parameter-decorators.ts
function validateParameterDecoratorArgs(token, target, propertyKey, parameterIndex, decoratorName) {
  if (token === null || token === void 0) {
    throw new Error(`@${decoratorName}: \u6CE8\u5165\u4EE4\u724C\u4E0D\u80FD\u4E3A null \u6216 undefined\u3002\u8BF7\u63D0\u4F9B\u6709\u6548\u7684\u6CE8\u5165\u4EE4\u724C\u3002`);
  }
  if (typeof parameterIndex !== "number" || parameterIndex < 0 || !Number.isInteger(parameterIndex)) {
    throw new Error(`@${decoratorName}: \u65E0\u6548\u7684\u53C2\u6570\u7D22\u5F15 ${parameterIndex}\u3002\u53C2\u6570\u7D22\u5F15\u5FC5\u987B\u662F\u975E\u8D1F\u6574\u6570\u3002`);
  }
  if (!target || typeof target !== "function" && typeof target !== "object") {
    throw new Error(`@${decoratorName}: \u65E0\u6548\u7684\u76EE\u6807\u5BF9\u8C61\u3002\u88C5\u9970\u5668\u53EA\u80FD\u5E94\u7528\u4E8E\u7C7B\u7684\u6784\u9020\u51FD\u6570\u53C2\u6570\u3002`);
  }
  if (propertyKey !== void 0) {
    throw new Error(`@${decoratorName}: \u6B64\u88C5\u9970\u5668\u53EA\u80FD\u7528\u4E8E\u6784\u9020\u51FD\u6570\u53C2\u6570\uFF0C\u4E0D\u80FD\u7528\u4E8E\u65B9\u6CD5\u53C2\u6570\u3002\u68C0\u6D4B\u5230\u7684\u5C5E\u6027\u952E: ${String(propertyKey)}`);
  }
  if (target.length !== void 0 && parameterIndex >= target.length) {
    console.warn(`@${decoratorName}: \u53C2\u6570\u7D22\u5F15 ${parameterIndex} \u53EF\u80FD\u8D85\u51FA\u6784\u9020\u51FD\u6570\u53C2\u6570\u8303\u56F4 (${target.length})\u3002\u8BF7\u786E\u8BA4\u6784\u9020\u51FD\u6570\u53C2\u6570\u6570\u91CF\u662F\u5426\u6B63\u786E\u3002`);
  }
}
__name(validateParameterDecoratorArgs, "validateParameterDecoratorArgs");
function Optional(token) {
  return function(target, propertyKey, parameterIndex) {
    validateParameterDecoratorArgs(token, target, propertyKey, parameterIndex, "Optional");
    return Inject(token, {
      optional: true
    })(target, propertyKey, parameterIndex);
  };
}
__name(Optional, "Optional");
function Self(token) {
  return function(target, propertyKey, parameterIndex) {
    validateParameterDecoratorArgs(token, target, propertyKey, parameterIndex, "Self");
    return Inject(token, {
      self: true
    })(target, propertyKey, parameterIndex);
  };
}
__name(Self, "Self");
function SkipSelf(token) {
  return function(target, propertyKey, parameterIndex) {
    validateParameterDecoratorArgs(token, target, propertyKey, parameterIndex, "SkipSelf");
    return Inject(token, {
      skipSelf: true
    })(target, propertyKey, parameterIndex);
  };
}
__name(SkipSelf, "SkipSelf");
function Host(token) {
  return function(target, propertyKey, parameterIndex) {
    validateParameterDecoratorArgs(token, target, propertyKey, parameterIndex, "Host");
    return Inject(token, {
      host: true
    })(target, propertyKey, parameterIndex);
  };
}
__name(Host, "Host");

// src/errors.ts
function _ts_decorate(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
__name(_ts_decorate, "_ts_decorate");
var ErrorWithStack = Error;
var AppError = class _AppError extends Error {
  static {
    __name(this, "AppError");
  }
  code;
  statusCode;
  meta;
  constructor(code, message, statusCode = 500, meta) {
    super(message), this.code = code, this.statusCode = statusCode, this.meta = meta;
    this.name = this.constructor.name;
    if (typeof ErrorWithStack.captureStackTrace === "function") {
      ErrorWithStack.captureStackTrace(this, this.constructor);
    }
  }
  toJSON() {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      statusCode: this.statusCode,
      ...this.meta && {
        meta: this.meta
      }
    };
  }
  static isAppError(error) {
    return error instanceof _AppError;
  }
};
var NoRetryError = class extends AppError {
  static {
    __name(this, "NoRetryError");
  }
  cause;
  constructor(message, cause) {
    super(`NoRetryError`, message, 500, {
      cause
    }), this.cause = cause;
  }
};
var DatabaseError = class extends AppError {
  static {
    __name(this, "DatabaseError");
  }
  constructor(message, operation, originalError) {
    const meta = {};
    if (operation) meta.operation = operation;
    if (originalError) {
      meta.originalError = originalError instanceof Error ? originalError.message : String(originalError);
    }
    super("DATABASE_ERROR", message, 500, Object.keys(meta).length > 0 ? meta : void 0);
  }
};
var ExternalServiceError = class extends AppError {
  static {
    __name(this, "ExternalServiceError");
  }
  constructor(service, message, originalError) {
    const meta = {
      service
    };
    if (originalError) {
      meta.originalError = originalError instanceof Error ? originalError.message : String(originalError);
    }
    super("EXTERNAL_SERVICE_ERROR", message, 502, meta);
  }
};
var ConfigurationError = class extends AppError {
  static {
    __name(this, "ConfigurationError");
  }
  constructor(message, configKey) {
    super("CONFIGURATION_ERROR", message, 500, configKey ? {
      configKey
    } : void 0);
  }
};
var BusinessRuleError = class extends AppError {
  static {
    __name(this, "BusinessRuleError");
  }
  constructor(message, rule, meta) {
    super("BUSINESS_RULE_VIOLATION", message, 422, {
      ...rule && {
        rule
      },
      ...meta
    });
  }
};
var ValidationError = class extends AppError {
  static {
    __name(this, "ValidationError");
  }
  constructor(message, fields) {
    super("VALIDATION_ERROR", message, 400, fields ? {
      fields
    } : void 0);
  }
};
var BadRequestError = class extends AppError {
  static {
    __name(this, "BadRequestError");
  }
  constructor(message, meta) {
    super("BAD_REQUEST", message, 400, meta);
  }
};
var UnauthorizedError = class extends AppError {
  static {
    __name(this, "UnauthorizedError");
  }
  constructor(message = "Authentication required") {
    super("UNAUTHORIZED", message, 401);
  }
};
var ForbiddenError = class extends AppError {
  static {
    __name(this, "ForbiddenError");
  }
  constructor(message = "Access forbidden") {
    super("FORBIDDEN", message, 403);
  }
};
var NotFoundError = class extends AppError {
  static {
    __name(this, "NotFoundError");
  }
  constructor(resource, identifier) {
    const message = identifier ? `${resource} with identifier '${identifier}' not found` : `${resource} not found`;
    super("NOT_FOUND", message, 404, {
      resource,
      identifier
    });
  }
};
var ConflictError = class extends AppError {
  static {
    __name(this, "ConflictError");
  }
  constructor(message, meta) {
    super("CONFLICT", message, 409, meta);
  }
};
var UnprocessableEntityError = class extends AppError {
  static {
    __name(this, "UnprocessableEntityError");
  }
  constructor(message, meta) {
    super("UNPROCESSABLE_ENTITY", message, 422, meta);
  }
};
var RateLimitError = class extends AppError {
  static {
    __name(this, "RateLimitError");
  }
  constructor(message = "Rate limit exceeded", retryAfter) {
    super("RATE_LIMIT_EXCEEDED", message, 429, retryAfter ? {
      retryAfter
    } : void 0);
  }
};
var InternalError = class extends AppError {
  static {
    __name(this, "InternalError");
  }
  constructor(message, originalError) {
    const meta = originalError ? {
      originalError: originalError instanceof Error ? originalError.message : String(originalError)
    } : void 0;
    super("INTERNAL_ERROR", message, 500, meta);
  }
};
var ServiceUnavailableError = class extends AppError {
  static {
    __name(this, "ServiceUnavailableError");
  }
  constructor(message = "Service temporarily unavailable", retryAfter) {
    super("SERVICE_UNAVAILABLE", message, 503, retryAfter ? {
      retryAfter
    } : void 0);
  }
};
var ErrorFactory = class {
  static {
    __name(this, "ErrorFactory");
  }
  /**
  * Create a validation error (400)
  * @param message - Error message
  * @param fields - Optional field-specific validation errors
  */
  validation(message, fields) {
    return new ValidationError(message, fields);
  }
  /**
  * Create an unauthorized error (401)
  * @param message - Optional custom message
  */
  unauthorized(message) {
    return new UnauthorizedError(message);
  }
  /**
  * Create a forbidden error (403)
  * @param message - Optional custom message
  */
  forbidden(message) {
    return new ForbiddenError(message);
  }
  /**
  * Create a not found error (404)
  * @param resource - Resource type (e.g., 'User', 'Post')
  * @param identifier - Optional resource identifier
  */
  notFound(resource, identifier) {
    return new NotFoundError(resource, identifier);
  }
  /**
  * Create a conflict error (409)
  * @param message - Error message
  * @param meta - Optional additional context
  */
  conflict(message, meta) {
    return new ConflictError(message, meta);
  }
  /**
  * Create an unprocessable entity error (422)
  * @param message - Error message
  * @param meta - Optional additional context
  */
  unprocessable(message, meta) {
    return new UnprocessableEntityError(message, meta);
  }
  /**
  * Create a rate limit error (429)
  * @param message - Optional custom message
  * @param retryAfter - Optional retry after seconds
  */
  rateLimit(message, retryAfter) {
    return new RateLimitError(message, retryAfter);
  }
  /**
  * Create an internal server error (500)
  * @param message - Error message
  * @param originalError - Optional original error that caused this
  */
  internal(message, originalError) {
    return new InternalError(message, originalError);
  }
  /**
  * Create a service unavailable error (503)
  * @param message - Optional custom message
  * @param retryAfter - Optional retry after seconds
  */
  unavailable(message, retryAfter) {
    return new ServiceUnavailableError(message, retryAfter);
  }
  /**
  * Create a database error (500)
  * @param message - Error message
  * @param operation - Optional database operation
  * @param originalError - Optional original error
  */
  database(message, operation, originalError) {
    return new DatabaseError(message, operation, originalError);
  }
  /**
  * Create an external service error (502)
  * @param service - Service name
  * @param message - Error message
  * @param originalError - Optional original error
  */
  externalService(service, message, originalError) {
    return new ExternalServiceError(service, message, originalError);
  }
  /**
  * Create a configuration error (500)
  * @param message - Error message
  * @param configKey - Optional configuration key
  */
  configuration(message, configKey) {
    return new ConfigurationError(message, configKey);
  }
  /**
  * Create a business rule violation error (422)
  * @param message - Error message
  * @param rule - Optional rule identifier
  * @param meta - Optional additional context
  */
  businessRule(message, rule, meta) {
    return new BusinessRuleError(message, rule, meta);
  }
};
ErrorFactory = _ts_decorate([
  Injectable()
], ErrorFactory);
var errorFactory = new ErrorFactory();
var BadRequestException = BadRequestError;
var NotFoundException = NotFoundError;
var UnauthorizedException = UnauthorizedError;
var ForbiddenException = ForbiddenError;

// src/error-serializer.ts
var ErrorSerializer = class {
  static {
    __name(this, "ErrorSerializer");
  }
  /**
  * 序列化单个错误对象
  *
  * 提取所有可序列化的属性，包括：
  * - Error 的 message, name, stack
  * - 自定义属性（type, statusCode, response）
  * - 嵌套的 cause 错误链
  */
  static serialize(error, includeStack = false) {
    if (!error) {
      return {
        message: "\u672A\u77E5\u9519\u8BEF",
        name: "UnknownError"
      };
    }
    if (this.isSerializedError(error)) {
      return error;
    }
    if (error instanceof Error) {
      const rawMessage = error.message || error.toString();
      const serialized = {
        message: typeof rawMessage === "string" ? rawMessage : JSON.stringify(rawMessage),
        name: error.name || "Error",
        ...includeStack && error.stack && {
          stack: error.stack
        }
      };
      const errorObj = error;
      if (errorObj.type) serialized.type = errorObj.type;
      if (errorObj.statusCode) serialized.statusCode = errorObj.statusCode;
      if (errorObj.response) serialized.response = errorObj.response;
      if (errorObj.originalError) serialized.originalError = errorObj.originalError;
      if (errorObj.cause) {
        serialized.cause = this.serialize(errorObj.cause, includeStack);
      }
      return serialized;
    }
    if (typeof error === "object") {
      const obj = error;
      const rawMessage = obj.message || String(error);
      return {
        message: typeof rawMessage === "string" ? rawMessage : JSON.stringify(rawMessage),
        name: obj.name || "Error",
        type: obj.type,
        statusCode: obj.statusCode,
        response: obj.response
      };
    }
    return {
      message: String(error),
      name: "Error"
    };
  }
  /**
  * 检查是否已经是序列化的错误格式
  */
  static isSerializedError(obj) {
    return obj && typeof obj === "object" && typeof obj.message === "string" && typeof obj.name === "string" && !obj.stack;
  }
  /**
  * 从错误链中提取最深层的真实错误信息
  *
  * 用途：前端显示时，显示最有用的错误信息
  */
  static extractDeepestError(error) {
    if (error.cause) {
      return this.extractDeepestError(error.cause);
    }
    return error;
  }
  /**
  * 获取完整的错误描述（包含链）
  *
  * 例如：
  * "NoRetryError: 登录态已过期 (caused by) WeiboError: API 返回错误"
  */
  static getFullDescription(error) {
    const parts = [
      error.message
    ];
    if (error.cause) {
      const causeDesc = this.getFullDescription(error.cause);
      parts.push(`caused by: ${causeDesc}`);
    }
    return parts.join(" ");
  }
};

// src/root.ts
var NULL_INJECTOR = new NullInjector();
function createInjector(providers, parent = NULL_INJECTOR, scope = "auto") {
  return EnvironmentInjector.createWithAutoProviders(providers, parent, scope);
}
__name(createInjector, "createInjector");
function createRootInjector(providers = []) {
  return EnvironmentInjector.createRootInjector(providers);
}
__name(createRootInjector, "createRootInjector");
function getRootInjector() {
  const inejctor = EnvironmentInjector.getRootInjector();
  if (inejctor) return inejctor;
  throw new Error(`root injector not found`);
}
__name(getRootInjector, "getRootInjector");
function resetRootInjector() {
  return EnvironmentInjector.resetRootInjector();
}
__name(resetRootInjector, "resetRootInjector");
function createPlatformInjector(providers = []) {
  return EnvironmentInjector.createPlatformInjector(providers);
}
__name(createPlatformInjector, "createPlatformInjector");
function getPlatformInjector() {
  const inejctor = EnvironmentInjector.getPlatformInjector();
  if (inejctor) return inejctor;
  throw new Error(`platform injector not found`);
}
__name(getPlatformInjector, "getPlatformInjector");
function resetPlatformInjector() {
  return EnvironmentInjector.resetPlatformInjector();
}
__name(resetPlatformInjector, "resetPlatformInjector");
function createApplicationInjector(providers = []) {
  return EnvironmentInjector.createApplicationInjector(providers);
}
__name(createApplicationInjector, "createApplicationInjector");
function createFeatureInjector(providers, parentInjector) {
  return EnvironmentInjector.createFeatureInjector(providers, parentInjector);
}
__name(createFeatureInjector, "createFeatureInjector");

// src/controller.ts
var PATH_METADATA = "path";
var METHOD_METADATA = "method";
var RequestMethod = /* @__PURE__ */ (function(RequestMethod2) {
  RequestMethod2[RequestMethod2["GET"] = 0] = "GET";
  RequestMethod2[RequestMethod2["POST"] = 1] = "POST";
  RequestMethod2[RequestMethod2["PUT"] = 2] = "PUT";
  RequestMethod2[RequestMethod2["DELETE"] = 3] = "DELETE";
  RequestMethod2[RequestMethod2["PATCH"] = 4] = "PATCH";
  return RequestMethod2;
})({});
var CONTROLLES = new InjectionToken(`CONTROLLES`);
var FEATURE_PROVIDERS = new InjectionToken(`FEATURE_PROVIDERS`);
function Controller(prefix) {
  return (target) => {
    if (typeof prefix === "function") {
      root.set([
        {
          provide: prefix,
          useClass: target
        },
        {
          provide: target,
          useClass: target
        },
        {
          provide: FEATURE_PROVIDERS,
          useValue: [
            {
              provide: prefix,
              useClass: target
            },
            {
              provide: target,
              useClass: target
            }
          ],
          multi: true
        }
      ]);
    } else {
      Reflect.defineMetadata(PATH_METADATA, prefix || ``, target);
      root.set([
        {
          provide: CONTROLLES,
          multi: true,
          useValue: target
        }
      ]);
    }
  };
}
__name(Controller, "Controller");
var RESPONSE_SCHEMA_METADATA = "response-schema";
var CONTENT_TYPE_METADATA = "content-type";
var SSE_METADATA = "sse";
function createHttpMethodDecorator(method) {
  return (pathOrOptionsOrSchema, zodOrContentType, contentType) => {
    return (target, propertyKey, descriptor) => {
      let routePath;
      let responseSchema;
      let finalContentType = "application/json";
      const isOptionsObject = pathOrOptionsOrSchema && typeof pathOrOptionsOrSchema === "object" && !("_def" in pathOrOptionsOrSchema) && !("parse" in pathOrOptionsOrSchema) && !("safeParse" in pathOrOptionsOrSchema);
      if (isOptionsObject) {
        const options = pathOrOptionsOrSchema;
        routePath = options.path || "/";
        responseSchema = options.schema;
        finalContentType = options.contentType || "application/json";
        if (options.sse) {
          const methodTarget2 = descriptor.value || target[propertyKey];
          Reflect.defineMetadata(SSE_METADATA, true, methodTarget2);
        }
      } else {
        const isZodSchema = pathOrOptionsOrSchema && typeof pathOrOptionsOrSchema === "object" && ("_def" in pathOrOptionsOrSchema || "parse" in pathOrOptionsOrSchema || "safeParse" in pathOrOptionsOrSchema);
        if (isZodSchema) {
          routePath = "/";
          responseSchema = pathOrOptionsOrSchema;
          finalContentType = typeof zodOrContentType === "string" ? zodOrContentType : "application/json";
        } else {
          routePath = pathOrOptionsOrSchema || "/";
          responseSchema = zodOrContentType && typeof zodOrContentType !== "string" ? zodOrContentType : void 0;
          finalContentType = contentType || "application/json";
        }
      }
      const methodTarget = descriptor.value || target[propertyKey];
      Reflect.defineMetadata(PATH_METADATA, routePath, methodTarget);
      Reflect.defineMetadata(METHOD_METADATA, method, methodTarget);
      Reflect.defineMetadata(CONTENT_TYPE_METADATA, finalContentType, methodTarget);
      if (responseSchema) {
        Reflect.defineMetadata(RESPONSE_SCHEMA_METADATA, responseSchema, methodTarget);
      }
    };
  };
}
__name(createHttpMethodDecorator, "createHttpMethodDecorator");
var Get = createHttpMethodDecorator(0);
var Post = createHttpMethodDecorator(1);
var Put = createHttpMethodDecorator(2);
var Delete = createHttpMethodDecorator(3);
var Patch = createHttpMethodDecorator(4);
var REQUEST = new InjectionToken(`REQUEST`);
var RESPONSE = new InjectionToken(`RESPONSE`);
var CONTEXT = new InjectionToken(`CONTEXT`);
var STREAM = new InjectionToken(`STREAM`);
var ParamType = /* @__PURE__ */ (function(ParamType2) {
  ParamType2["PARAM"] = "param";
  ParamType2["QUERY"] = "query";
  ParamType2["BODY"] = "body";
  return ParamType2;
})({});
var ROUTE_ARGS_METADATA = "route-args";
var MIDDLEWARE_METADATA = "middleware";
var INTERCEPTORS_METADATA = "interceptors";
var OPENAPI_DESCRIPTION_METADATA = "openapi:description";
var OPENAPI_TAGS_METADATA = "openapi:tags";
function createParamDecorator(type) {
  return (key, zod) => (target, propertyKey, parameterIndex) => {
    if (!propertyKey) {
      throw new Error("\u53C2\u6570\u88C5\u9970\u5668\u53EA\u80FD\u7528\u4E8E\u65B9\u6CD5\u53C2\u6570");
    }
    let fieldName;
    let schema;
    const isZodSchema = key && typeof key === "object" && ("_def" in key || "parse" in key || "safeParse" in key);
    if (isZodSchema) {
      fieldName = void 0;
      schema = key;
    } else {
      fieldName = key;
      schema = zod;
    }
    const methodTarget = target;
    const existingMetadata = Reflect.getMetadata(ROUTE_ARGS_METADATA, methodTarget[propertyKey]) || {};
    const metadataKey = `${type}:${parameterIndex}`;
    existingMetadata[metadataKey] = {
      index: parameterIndex,
      type,
      key: fieldName,
      zod: schema
    };
    Reflect.defineMetadata(ROUTE_ARGS_METADATA, existingMetadata, methodTarget[propertyKey]);
  };
}
__name(createParamDecorator, "createParamDecorator");
var Param = createParamDecorator("param");
var Query = createParamDecorator("query");
var Body = createParamDecorator("body");
function RequirePermissions(permissions) {
  return (target, propertyKey, descriptor) => {
    const methodTarget = descriptor.value || target[propertyKey];
    Reflect.defineMetadata(MIDDLEWARE_METADATA, {
      permissions
    }, methodTarget);
  };
}
__name(RequirePermissions, "RequirePermissions");
function ApiDescription(description, tags) {
  return (target, propertyKey, descriptor) => {
    const methodTarget = descriptor.value || target[propertyKey];
    Reflect.defineMetadata(OPENAPI_DESCRIPTION_METADATA, description, methodTarget);
    if (tags) {
      Reflect.defineMetadata(OPENAPI_TAGS_METADATA, tags, methodTarget);
    }
  };
}
__name(ApiDescription, "ApiDescription");

// src/logger.ts
function _ts_decorate2(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
__name(_ts_decorate2, "_ts_decorate");
function _ts_metadata(k, v) {
  if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
__name(_ts_metadata, "_ts_metadata");
function _ts_param(paramIndex, decorator) {
  return function(target, key) {
    decorator(target, key, paramIndex);
  };
}
__name(_ts_param, "_ts_param");
var LoggerLevel = /* @__PURE__ */ (function(LoggerLevel2) {
  LoggerLevel2[LoggerLevel2["trace"] = 0] = "trace";
  LoggerLevel2[LoggerLevel2["debug"] = 1] = "debug";
  LoggerLevel2[LoggerLevel2["info"] = 2] = "info";
  LoggerLevel2[LoggerLevel2["warn"] = 3] = "warn";
  LoggerLevel2[LoggerLevel2["error"] = 4] = "error";
  return LoggerLevel2;
})({});
var LOGGER_LEVEL = new InjectionToken(`LOGGER_LEVEL`, {
  factory: /* @__PURE__ */ __name(() => 2, "factory")
});
var Logger = class {
  static {
    __name(this, "Logger");
  }
  level;
  name = `default`;
  constructor(level = 2) {
    this.level = level;
  }
  _log(level, ...args) {
    if (level >= this.level) {
      switch (level) {
        case 0:
          console.trace(...args);
          break;
        case 1:
          console.debug(...args);
          break;
        case 2:
          console.info(...args);
          break;
        case 3:
          console.warn(...args);
          break;
        case 4:
          console.error(...args);
          break;
        default:
          console.log(...args);
          break;
      }
    }
  }
  trace(...args) {
    this._log(0, ...args);
  }
  verbose(...args) {
    this._log(0, ...args);
  }
  debug(...args) {
    this._log(1, ...args);
  }
  info(...args) {
    this._log(2, ...args);
  }
  log(...args) {
    this._log(2, ...args);
  }
  warn(...args) {
    this._log(3, ...args);
  }
  error(...args) {
    this._log(4, ...args);
  }
};
Logger = _ts_decorate2([
  Injectable(),
  _ts_param(0, Inject(LOGGER_LEVEL, {
    optional: true
  })),
  _ts_metadata("design:type", Function),
  _ts_metadata("design:paramtypes", [
    Number
  ])
], Logger);
var logger = new Logger(2);
function createLogger(name, level = 2) {
  const logger2 = new Logger(level);
  logger2.name = name;
  return logger2;
}
__name(createLogger, "createLogger");

// src/error-handler.ts
function _ts_decorate3(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
__name(_ts_decorate3, "_ts_decorate");
var ErrorHandler = class {
  static {
    __name(this, "ErrorHandler");
  }
  logger = createLogger("ErrorHandler");
  handle(error) {
    this.logger.error(error);
  }
};
ErrorHandler = _ts_decorate3([
  Injectable()
], ErrorHandler);

// src/util.ts
function isObservable(obs) {
  return obs && typeof obs.subscribe === "function";
}
__name(isObservable, "isObservable");
function isPromise(obs) {
  return obs && typeof obs.then === "function";
}
__name(isPromise, "isPromise");

// src/cors.ts
var DEFAULT_CORS_CONFIG = {
  origin: "*",
  allowMethods: [
    "GET",
    "POST",
    "PUT",
    "DELETE",
    "PATCH",
    "OPTIONS"
  ],
  allowHeaders: [
    "Content-Type",
    "Authorization",
    "X-Requested-With",
    "X-Request-ID",
    "Accept",
    "Origin"
  ],
  credentials: true,
  maxAge: 86400
};
function getRequestOrigin(req) {
  const origin = req.headers["origin"];
  const expoOrigin = req.headers["expo-origin"];
  return origin || expoOrigin || null;
}
__name(getRequestOrigin, "getRequestOrigin");
function isOriginAllowed(origin, allowedOrigins) {
  if (allowedOrigins.includes("*")) {
    return true;
  }
  if (allowedOrigins.includes(origin)) {
    return true;
  }
  return allowedOrigins.some((allowed) => origin.startsWith(allowed));
}
__name(isOriginAllowed, "isOriginAllowed");
function addCorsHeaders(res, req, config = DEFAULT_CORS_CONFIG) {
  const requestOrigin = getRequestOrigin(req);
  if (typeof config.origin === "string") {
    if (config.origin === "*") {
      res.setHeader("Access-Control-Allow-Origin", "*");
    } else {
      res.setHeader("Access-Control-Allow-Origin", config.origin);
    }
  } else if (Array.isArray(config.origin)) {
    if (requestOrigin && isOriginAllowed(requestOrigin, config.origin)) {
      res.setHeader("Access-Control-Allow-Origin", requestOrigin);
      res.setHeader("Vary", "Origin");
    } else if (config.origin.includes("*")) {
      res.setHeader("Access-Control-Allow-Origin", "*");
    }
  }
  res.setHeader("Access-Control-Allow-Methods", config.allowMethods.join(", "));
  res.setHeader("Access-Control-Allow-Headers", config.allowHeaders.join(", "));
  res.setHeader("Access-Control-Allow-Credentials", String(config.credentials));
  res.setHeader("Access-Control-Max-Age", String(config.maxAge));
}
__name(addCorsHeaders, "addCorsHeaders");
export {
  APP_INITIALIZER,
  ApiDescription,
  AppError,
  BadRequestError,
  BadRequestException,
  Body,
  BusinessRuleError,
  CONTENT_TYPE_METADATA,
  CONTEXT,
  CONTROLLES,
  ConfigurationError,
  ConflictError,
  Controller,
  DEFAULT_CORS_CONFIG,
  DatabaseError,
  Delete,
  EnvironmentInjector,
  ErrorFactory,
  ErrorHandler,
  ErrorSerializer,
  ExternalServiceError,
  FEATURE_PROVIDERS,
  ForbiddenError,
  ForbiddenException,
  ForwardRef,
  Get,
  Host,
  HostAttributeToken,
  INTERCEPTORS_METADATA,
  Inject,
  Injectable,
  InjectionToken,
  Injector,
  InternalError,
  InternalInjectFlags,
  LOGGER_LEVEL,
  Logger,
  LoggerLevel,
  METHOD_METADATA,
  MIDDLEWARE_METADATA,
  MapInjectionToken,
  NULL_INJECTOR,
  NoRetryError,
  NotFoundError,
  NotFoundException,
  NullInjector,
  OPENAPI_DESCRIPTION_METADATA,
  OPENAPI_TAGS_METADATA,
  OnInit,
  Optional,
  PATH_METADATA,
  Param,
  ParamType,
  Patch,
  Post,
  Put,
  Query,
  REQUEST,
  RESPONSE,
  RESPONSE_SCHEMA_METADATA,
  ROUTE_ARGS_METADATA,
  RateLimitError,
  RecordInjectionToken,
  RequestMethod,
  RequirePermissions,
  SSE_METADATA,
  STREAM,
  Self,
  ServiceUnavailableError,
  SetInjectionToken,
  SkipSelf,
  UnauthorizedError,
  UnauthorizedException,
  UnprocessableEntityError,
  ValidationError,
  addCorsHeaders,
  combineInjectFlags,
  convertFlagsToInjectOptions,
  convertInjectOptionsToFlags,
  createApplicationInjector,
  createFeatureInjector,
  createHostAttributeToken,
  createInjector,
  createLogger,
  createPlatformInjector,
  createRootInjector,
  errorFactory,
  flagsToString,
  forwardRef,
  getInjectMetadata,
  getInjectOptionsMetadata,
  getInjectableMetadata,
  getPlatformInjector,
  getRequestOrigin,
  getRootInjector,
  getUnifiedInjectMetadata,
  hasFlag,
  hasInjectMetadata,
  hasOnInitMetadata,
  isForwardRef,
  isHostAttributeToken,
  isInjectable,
  isObservable,
  isOnDestroy,
  isOnInit,
  isOriginAllowed,
  isPromise,
  logger,
  resetPlatformInjector,
  resetRootInjector,
  resolveForwardRef,
  root,
  validateInjectOptionsConflicts
};
//# sourceMappingURL=index.mjs.map